import React from "react";
import { Link } from "react-router-dom";
import { Sparkle, MagnifyingGlass, ArrowRight, X } from "@phosphor-icons/react";
import { apiPost } from "../lib/http";
import { formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { toast } from "sonner";

// ATLAS Buyer — Conversational Search widget.
// Per ADR-0007 Module B · FastStructuredModel. Provider-agnostic on backend.
// Per ADR-0008: professional, direct, actionable, no chatbot fluff.
const EXAMPLES = [
  "Busco casa en San Pedro con jardín y 3 recámaras hasta 12M",
  "Departamento en renta en Roma o Condesa con estacionamiento",
  "Terreno amplio en Monterrey para construir, hasta 5 millones",
];

export const ATLASBuyer = () => {
  const [query, setQuery] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [response, setResponse] = React.useState(null);
  const [sessionId] = React.useState(() => `atlas-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);

  const submit = async (text) => {
    const q = (text ?? query).trim();
    if (!q) return;
    setQuery(q);
    setLoading(true);
    try {
      const data = await apiPost("/atlas/buyer/search", { query: q, session_id: sessionId });
      setResponse(data);
    } catch (err) {
      toast.error(formatApiError(err, "ATLAS no está disponible ahora"));
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setQuery("");
    setResponse(null);
  };

  return (
    <div
      className="bg-[#1A1A1A] rounded-2xl border border-[#C8A24A]/20 p-6 md:p-8"
      data-testid="atlas-buyer-panel"
    >
      <div className="flex items-start gap-4 mb-6">
        <div className="w-11 h-11 rounded-xl bg-[#C8A24A]/10 flex items-center justify-center flex-shrink-0 border border-[#C8A24A]/20">
          <Sparkle size={20} weight="fill" className="text-[#C8A24A]" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs uppercase tracking-[0.25em] text-[#C8A24A] font-semibold">
            ATLAS · Director Comercial
          </p>
          <h3 className="font-heading text-2xl text-[#C8A24A] mt-1 leading-tight">
            Cuéntame qué propiedad buscas
          </h3>
          <p className="text-sm text-[#C8A24A]/70 mt-2 leading-relaxed">
            Descríbelo como se lo dirías a un asesor: zona, presupuesto, cuartos, lo que sea importante. Yo encuentro lo que encaja.
          </p>
        </div>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); submit(); }}
        className="flex flex-col sm:flex-row gap-2"
        data-testid="atlas-buyer-form"
      >
        <div className="flex-1 relative">
          <MagnifyingGlass size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#C8A24A]/60" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Ej. Casa en San Pedro con jardín hasta 12M"
            className="pl-11 bg-[#0A0A0A] border-[#C8A24A]/20 text-[#C8A24A] placeholder:text-[#C8A24A]/40 h-12 rounded-xl"
            data-testid="atlas-buyer-input"
          />
        </div>
        <Button
          type="submit"
          disabled={loading || !query.trim()}
          className="bg-[#C8A24A] hover:bg-[#B88C2D] text-[#111111] h-12 px-6 rounded-xl font-semibold"
          data-testid="atlas-buyer-submit"
        >
          {loading ? "Analizando..." : "Buscar"}
        </Button>
      </form>

      {!response && !loading && (
        <div className="mt-5" data-testid="atlas-buyer-examples">
          <p className="text-xs uppercase tracking-[0.2em] text-[#C8A24A]/50 font-semibold mb-3">
            Prueba con
          </p>
          <div className="flex flex-wrap gap-2">
            {EXAMPLES.map((ex, i) => (
              <button
                key={i}
                onClick={() => submit(ex)}
                className="text-xs px-4 py-2 rounded-full bg-[#0A0A0A] border border-[#C8A24A]/20 text-[#C8A24A]/80 hover:border-[#C8A24A] hover:text-[#C8A24A] transition-colors min-h-[36px]"
                data-testid={`atlas-example-${i}`}
              >
                {ex}
              </button>
            ))}
          </div>
        </div>
      )}

      {response && (
        <div className="mt-6 pt-5 border-t border-[#C8A24A]/20" data-testid="atlas-buyer-result">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="min-w-0">
              <p className="text-xs uppercase tracking-wider text-[#C8A24A]/60 font-semibold mb-1">Criterio interpretado</p>
              <p className="text-sm text-[#C8A24A]" data-testid="atlas-summary">
                {response.criteria_summary || "Búsqueda genérica"}
              </p>
              {response.explanation && (
                <p className="text-xs text-[#C8A24A]/60 mt-2">{response.explanation}</p>
              )}
            </div>
            <button
              onClick={reset}
              className="p-1.5 rounded-md hover:bg-[#C8A24A]/10 text-[#C8A24A]/60"
              aria-label="Nueva búsqueda"
              data-testid="atlas-reset"
            >
              <X size={16} />
            </button>
          </div>

          <p className="text-xs text-[#C8A24A]/70 mb-3" data-testid="atlas-result-count">
            {response.result_count} propiedad{response.result_count === 1 ? "" : "es"} coinciden
          </p>

          {response.result_count === 0 ? (
            <p className="text-sm text-[#C8A24A]/60 py-3">
              No encontramos propiedades con ese criterio. Ajusta tu búsqueda o explora el catálogo completo.
            </p>
          ) : (
            <div className="space-y-2">
              {response.results.slice(0, 4).map((p) => (
                <Link
                  key={p.id}
                  to={`/propiedades/${p.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg bg-[#0A0A0A] border border-[#C8A24A]/10 hover:border-[#C8A24A]/40 transition-colors"
                  data-testid={`atlas-result-${p.id}`}
                >
                  <div className="w-14 h-14 rounded-md bg-[#1A1A1A] overflow-hidden flex-shrink-0">
                    {p.images?.[0] && <img src={p.images[0].startsWith("http") ? p.images[0] : `${window.location.origin.replace(/:\d+$/, "")}${p.images[0]}`} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-[#C8A24A] truncate font-medium">{p.title}</p>
                    <p className="text-xs text-[#C8A24A]/60 truncate">{p.city} · {new Intl.NumberFormat("es-MX", { style: "currency", currency: "MXN", maximumFractionDigits: 0 }).format(p.price)}</p>
                  </div>
                  <ArrowRight size={16} className="text-[#C8A24A]/50" />
                </Link>
              ))}
              {response.result_count > 4 && (
                <p className="text-xs text-[#C8A24A]/50 text-center pt-2">
                  +{response.result_count - 4} propiedades más en resultados completos abajo
                </p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
